import { TOKEN_STORAGE_KEY, USER_DATA_STORAGE_KEY, SUPERVISOR_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY, UTM_TRACKER } from "@core/constants";
import { StorageManager } from "@core/managers/storage.manager";
import { ISupervisedEtab, ISupervisor } from "../models";

/**
 * Core User Data Service in charge of localstorage
 * @class UserStorageManager
 */
export class UserStorageManager {
  /**
   * Get token from localStorage
   * @returns {String} token
   */
  static async getToken() {
    return StorageManager.getItem(TOKEN_STORAGE_KEY);
  }

  /**
   * Get refreshToken from localStorage
   * @returns {String} refreshToken
   */
  static async getRefreshToken() {
    return StorageManager.getItem(REFRESH_TOKEN_STORAGE_KEY);
  }

  /**
   * Set token into localStorage
   * @param {String} data
   */
  static async setToken(data) {
    return StorageManager.setItem(TOKEN_STORAGE_KEY, data);
  }

  /**
   * Set refreshToken into localStorage
   * @param {String} data
   */
  static async setRefreshToken(data) {
    return StorageManager.setItem(REFRESH_TOKEN_STORAGE_KEY, data);
  }

  /**
   * Remove token from localStorage
   */
  static async removeToken() {
    return StorageManager.removeItem(TOKEN_STORAGE_KEY);
  }

  /**
   * Remove refreshToken from localStorage
   */
  static async removeRefreshToken() {
    return StorageManager.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  }

  /**
   * Get client type based on SupervisorData from localStorage
   * @returns {String}
   */
  static async getClientType() {
    let clientType = "default";
    const supervisorData = await StorageManager.getItem(SUPERVISOR_STORAGE_KEY);
    if (!!supervisorData && supervisorData[ISupervisedEtab.masterEtabId]) {
      // multi_etab has a master etab key
      clientType = "multi_etab";
    }
    if (!!supervisorData && supervisorData[ISupervisor.supervisorId]) {
      // ghost mode has a master etab key
      clientType = "ghost_mode";
    }
    return clientType;
  }

  /**
   * Get SupervisorData from localStorage
   * @returns {Object}
   */
  static async getSupervisorData() {
    return StorageManager.getItem(SUPERVISOR_STORAGE_KEY);
  }

  /**
   * Set SupervisorData into localStorage
   * @param {Object}
   */
  static async setSupervisorData(data) {
    return StorageManager.setItem(SUPERVISOR_STORAGE_KEY, data);
  }

  /**
   * Remove SupervisorData from localStorage
   */
  static async removeSupervisorData() {
    return StorageManager.removeItem(SUPERVISOR_STORAGE_KEY);
  }

  /**
   * Get user from localStorage
   * @returns {String} user
   */
  static async getUser() {
    return StorageManager.getItem(USER_DATA_STORAGE_KEY);
  }

  /**
   * Set user into localStorage
   * @param {String} data
   */
  static async setUser(data) {
    StorageManager.setItem(USER_DATA_STORAGE_KEY, data);
  }

  /**
   * Set cookie into localStorage
   * @param {String} key
   * @param {String} data
   */
  static async setCookie(key, data) {
    StorageManager.setItem(key, data);
  }

  /**
   * Get cookie into localStorage
   * @param {String} key
   */
  static async getCookie(key) {
    return StorageManager.getItem(key);
  }

  /**
   * Remove user from localStorage
   */
  static async removeUser() {
    StorageManager.removeItem(USER_DATA_STORAGE_KEY);
  }

  /**
   * Set utm tracker into localStorage
   * @param {String} data
   */
  static async setUtmTracker(data) {
    return StorageManager.setItem(UTM_TRACKER, data);
  }

  /**
   * Get utm tracker from localStorage
   * @returns {Promise<string>} utmValue
   */
  static async getUtmTracker() {
    return StorageManager.getItem(UTM_TRACKER);
  }

  /**
   * Remove utm tracker from localStorage
   */
  static async removeUtmTracker() {
    StorageManager.removeItem(UTM_TRACKER);
  }
}
